import {Material} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Skeleton, Box, BoxProps} from '@mui/material'

import {toPixel} from '../../../../../shared/helpers/utils'
import {
  useMaterialStorageDataQuery,
  useStockDevelopmentQuery
} from '../../../../../shared/hooks/api'
import {useCurrentScheduleQuery} from '../../../../../shared/hooks/api/schedules/useCurrentScheduleQuery'
import {MomentRange} from '../../../../../shared/selectors/time'
import {usePlanningChartScheduleData} from '../../../../page-planning/dailyPlanning/PlanningChartScheduleDataProvider'
import {usePlanningChartStartEnd} from '../../../../page-planning/dailyPlanning/PlanningChartStartEndProvider'

import {OptimizerStockLevelsChartContent} from './OptimizerStockLevelsChartContent'

type OptimizerStockLevelsChartProps = {
  material: Material
  width: number
} & BoxProps

export const OptimizerStockLevelsChart = ({
  material,
  width,
  ...restProps
}: OptimizerStockLevelsChartProps) => {
  const height = 90

  const {startOfPlan, endOfPlan, startOfChart, endOfChart} = usePlanningChartStartEnd()

  const planRange = [startOfPlan, endOfPlan] as MomentRange
  const chartRange = [startOfChart, endOfChart] as MomentRange

  // Obtaining the id of optimized plan if one is selected
  const {scheduleId} = usePlanningChartScheduleData()
  const isOptimizedPlanSelected = scheduleId !== undefined

  // Obtaining stock development data for the current manual plan if optimized plan is selected
  const {data: manualSchedule} = useCurrentScheduleQuery({range: chartRange})
  const {data: stockDevelopmentProductionPlan} = useStockDevelopmentQuery({
    materialIds: isOptimizedPlanSelected && manualSchedule ? [material.id] : [],
    scheduleId: manualSchedule?.scheduleId,
    timeframe: planRange
  })

  const {data: stockDevelopmentData} = useStockDevelopmentQuery({
    materialIds: [material.id],
    scheduleId,
    timeframe: planRange
  })
  const {data: storageData} = useMaterialStorageDataQuery({materialId: material.id})

  if (stockDevelopmentData && !stockDevelopmentData[material.id]) {
    throw new Error(`Material ${material.id} not found in stock development data`)
  }

  return (
    <Box {...restProps} width="auto" {...dataTestId(`optimizer-stock-levels-chart-${material.id}`)}>
      {storageData && stockDevelopmentData ? (
        <OptimizerStockLevelsChartContent
          width={width}
          height={height}
          minTargetLevel={storageData.minTargetLevel}
          minTargetLevelWeekend={storageData.minTargetLevelWeekend}
          storageCapacity={storageData.storageCapacity}
          stockDevelopmentData={stockDevelopmentData[material.id]}
          stockDevelopmentSecondaryData={
            isOptimizedPlanSelected && stockDevelopmentProductionPlan
              ? stockDevelopmentProductionPlan[material.id]
              : undefined
          }
        />
      ) : (
        <Skeleton width={toPixel(width)} height={height} variant="rectangular" />
      )}
    </Box>
  )
}
